import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { TabGroup, TabButton } from '../../components/ui/Tabs/index.js';
import { useKnowledgeWikiDocuments } from '../../hooks/useKnowledgeWiki.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { ErrorState } from '../../components/feedback/ErrorState/index.js';
import { KpiTableSkeleton } from '../../components/feedback/KpiTableSkeleton/index.js';
import type { KnowledgeWikiListRow } from '../../types/knowledge-wiki.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business コーディング規約 wiki の一覧ページ。カテゴリ・タグ・全文検索で絞り込み、未記入の記事を可視化する
 * @graph-connects useKnowledgeWikiDocuments [calls] ドキュメント一覧フック呼び出し
 */
export const Route = createFileRoute('/knowledge-wiki/')({
  component: KnowledgeWikiListPage,
});

/** 更新日を YYYY-MM-DD で表示する。未設定はハイフン。 @graph-connects none */
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

/**
 * 記事 1 件のカード。タイトル・説明・タグ・未記入バッジを出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business wiki 一覧の 1 行を描画し、詳細ページへのリンクと未記入状態を示す
 * @graph-connects none
 */
function DocumentRow({
  row,
  tagLabels,
}: {
  row: KnowledgeWikiListRow;
  tagLabels: Map<string, string>;
}) {
  const { t } = useTranslation();
  return (
    <Link
      to="/knowledge-wiki/$id"
      params={{ id: row.id }}
      className="block rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3 transition-colors hover:border-[var(--color-border-hover)] hover:bg-[var(--color-bg-hover)]"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-[var(--color-text-primary)]">{row.title}</span>
            {row.missingFields.length > 0 && (
              <Badge variant="warning">
                {t('knowledgeWiki.incompleteBadge', { count: row.missingFields.length })}
              </Badge>
            )}
            {row.localeCode !== 'ja' && <Badge variant="info">{row.localeCode}</Badge>}
          </div>
          {row.description && (
            <p className="mt-1 text-sm text-[var(--color-text-secondary)] line-clamp-2">
              {row.description}
            </p>
          )}
          {row.tagIds.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {row.tagIds.map((tagId) => (
                <span
                  key={tagId}
                  className="rounded px-1.5 py-0.5 text-xs text-[var(--color-text-muted)] bg-[var(--color-bg-base)]"
                >
                  {tagLabels.get(tagId) ?? tagId}
                </span>
              ))}
            </div>
          )}
        </div>
        <span className="shrink-0 text-xs text-[var(--color-text-muted)]">
          {formatDate(row.updatedAt)}
        </span>
      </div>
    </Link>
  );
}

/**
 * コーディング規約 wiki 一覧ページ
 * @graph-connects none
 */
function KnowledgeWikiListPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('en') ? 'en' : 'ja';
  const [category, setCategory] = useState<string>('');
  const [tagId, setTagId] = useState<string>('');
  const [q, setQ] = useState<string>('');
  const [incompleteOnly, setIncompleteOnly] = useState(false);

  // 入力欄は q をそのまま表示し、取得のトリガーだけ遅らせる。
  // 打鍵ごとに fetch すると一覧全体が再描画され、入力が引っかかる。
  const debouncedQ = useDebouncedValue(q);

  const { data, isLoading, isError, refetch } = useKnowledgeWikiDocuments({
    locale,
    category: category || undefined,
    tagId: tagId || undefined,
    q: debouncedQ.trim() || undefined,
    incompleteOnly,
  });

  const tagLabels = useMemo(
    () => new Map((data?.tags ?? []).map((tag) => [tag.id, tag.label])),
    [data],
  );

  const header = (
    <div>
      <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
        {t('knowledgeWiki.title')}
      </h1>
      <p className="mt-1 text-[var(--color-text-secondary)]">{t('knowledgeWiki.description')}</p>
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        {header}
        <KpiTableSkeleton />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-6 animate-fade-in">
        {header}
        <ErrorState
          message={t('knowledgeWiki.loadError')}
          onRetry={() => {
            void refetch();
          }}
        />
      </div>
    );
  }

  const tagOptions = [
    { value: '', label: t('knowledgeWiki.allTags') },
    ...data.tags.map((tag) => ({
      value: tag.id,
      label: `${tag.label} (${String(tag.count)})`,
    })),
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {header}

      <div className="flex flex-wrap items-center gap-3 text-sm text-[var(--color-text-secondary)]">
        <span>{t('knowledgeWiki.totalCount', { count: data.total })}</span>
        <button
          type="button"
          onClick={() => {
            setIncompleteOnly((prev) => !prev);
          }}
          aria-pressed={incompleteOnly}
          className={`rounded-full border px-3 py-1 text-xs transition-colors ${
            incompleteOnly
              ? 'border-[var(--color-warning)] text-[var(--color-warning)]'
              : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-border-hover)]'
          }`}
        >
          {t('knowledgeWiki.incompleteFilter', { count: data.incompleteCount })}
        </button>
      </div>

      <TabGroup ariaLabel={t('knowledgeWiki.categorySwitchLabel')}>
        <TabButton
          active={category === ''}
          label={t('knowledgeWiki.allCategories')}
          onClick={() => {
            setCategory('');
          }}
        />
        {data.categories.map((c) => (
          <TabButton
            key={c.category}
            active={category === c.category}
            label={`${t(`knowledgeWiki.category.${c.category}`, { defaultValue: c.category })} (${String(c.count)})`}
            onClick={() => {
              setCategory(c.category);
            }}
          />
        ))}
      </TabGroup>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="flex-1">
          <Input
            type="search"
            value={q}
            placeholder={t('knowledgeWiki.searchPlaceholder')}
            aria-label={t('knowledgeWiki.searchPlaceholder')}
            onChange={(e) => {
              setQ(e.target.value);
            }}
          />
        </div>
        <div className="sm:w-64">
          <Select
            options={tagOptions}
            value={tagId}
            aria-label={t('knowledgeWiki.allTags')}
            fullWidth
            onChange={(e) => {
              setTagId(e.target.value);
            }}
          />
        </div>
      </div>

      {data.rows.length === 0 ? (
        <Card>
          <CardContent>
            <p className="py-8 text-center text-[var(--color-text-muted)]">
              {t('knowledgeWiki.empty')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.rows.map((row) => (
            <DocumentRow key={row.id} row={row} tagLabels={tagLabels} />
          ))}
        </div>
      )}
    </div>
  );
}
